'use client'

import type { CSSProperties, ComponentType } from 'react'
import {
  Check, Clover, Coins, Crown, DoorOpen, Dices, Flame, Gamepad2, Gift, Heart, HeartCrack, Link2, Lock, Medal, Mic, PartyPopper,
  Rocket, Sparkles, Star, Swords, Target, Trophy, Users, Zap, Camera, Gem, Brain, Timer, Sprout, Moon, Phone, Video, Handshake, Dumbbell, Smile,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// SVG "poster" icons - one place that replaces the plain phone emojis in the store, games, hub and prize screens.
// Each icon is a glossy gradient tile with a crisp vector glyph on top (no emoji font, looks the same on every phone).
// Chat reactions / stickers / game chat keep their animated emojis (components/emoji3d.tsx) - those are NOT touched.
// ---------------------------------------------------------------------------
type Glyph = ComponentType<{ size?: number; strokeWidth?: number; color?: string }>
type Def = { glyph: Glyph; a: string; b: string }

const DEFS: Record<string, Def> = {
  gift: { glyph: Gift, a: '#ff7ab8', b: '#c2127a' },
  party: { glyph: PartyPopper, a: '#ffb347', b: '#ff4f81' },
  fire: { glyph: Flame, a: '#ffb347', b: '#ef4444' },
  trophy: { glyph: Trophy, a: '#ffd54a', b: '#f59e0b' },
  crown: { glyph: Crown, a: '#ffd54a', b: '#d97706' },
  heart: { glyph: Heart, a: '#ff6b94', b: '#e11d48' },
  heartbreak: { glyph: HeartCrack, a: '#94a3b8', b: '#475569' },
  check: { glyph: Check, a: '#4ade80', b: '#15803d' },
  lock: { glyph: Lock, a: '#94a3b8', b: '#475569' },
  bolt: { glyph: Zap, a: '#fde047', b: '#f59e0b' },
  clover: { glyph: Clover, a: '#4ade80', b: '#15803d' },
  gamepad: { glyph: Gamepad2, a: '#a78bfa', b: '#6d28d9' },
  medal: { glyph: Medal, a: '#fbbf24', b: '#b45309' },
  gold: { glyph: Medal, a: '#ffe066', b: '#e6a700' },
  silver: { glyph: Medal, a: '#e5e7eb', b: '#8b95a5' },
  bronze: { glyph: Medal, a: '#e8a36b', b: '#9a5a2a' },
  door: { glyph: DoorOpen, a: '#94a3b8', b: '#475569' },
  star: { glyph: Star, a: '#ffe066', b: '#f59e0b' },
  coin: { glyph: Coins, a: '#ffe066', b: '#e6a700' },
  rocket: { glyph: Rocket, a: '#60a5fa', b: '#6d28d9' },
  dice: { glyph: Dices, a: '#fb923c', b: '#ea580c' },
  sparkle: { glyph: Sparkles, a: '#f0abfc', b: '#a21caf' },
  swords: { glyph: Swords, a: '#94a3b8', b: '#334155' },
  target: { glyph: Target, a: '#fb7185', b: '#be123c' },
  users: { glyph: Users, a: '#38bdf8', b: '#0369a1' },
  camera: { glyph: Camera, a: '#a78bfa', b: '#6d28d9' },
  mic: { glyph: Mic, a: '#f472b6', b: '#be185d' },
  link: { glyph: Link2, a: '#38bdf8', b: '#0369a1' },
  gem: { glyph: Gem, a: '#67e8f9', b: '#0e7490' },
  brain: { glyph: Brain, a: '#f9a8d4', b: '#be185d' },
  timer: { glyph: Timer, a: '#fdba74', b: '#c2410c' },
  sprout: { glyph: Sprout, a: '#86efac', b: '#15803d' },
  moon: { glyph: Moon, a: '#a5b4fc', b: '#4338ca' },
  phone: { glyph: Phone, a: '#4ade80', b: '#15803d' },
  video: { glyph: Video, a: '#60a5fa', b: '#1d4ed8' },
  handshake: { glyph: Handshake, a: '#fbbf24', b: '#b45309' },
  muscle: { glyph: Dumbbell, a: '#fbbf24', b: '#b45309' },
  smile: { glyph: Smile, a: '#ffe066', b: '#f59e0b' },
}

/** Phone emoji -> poster icon name (used where data still stores an emoji, e.g. prize badges). Unknown emoji -> a star. */
const EMOJI_TO_NAME: Record<string, string> = {
  '🎁': 'gift', '🎉': 'party', '🔥': 'fire', '🏆': 'trophy', '👑': 'crown', '💛': 'heart', '❤': 'heart', '♥': 'heart', '💔': 'heartbreak',
  '✅': 'check', '✓': 'check', '✔': 'check', '🔒': 'lock', '⚡': 'bolt', '🍀': 'clover', '🎮': 'gamepad', '🥇': 'gold', '🥈': 'silver',
  '🥉': 'bronze', '🏅': 'medal', '🚪': 'door', '🌟': 'star', '⭐': 'star', '🪙': 'coin', '🚀': 'rocket', '🎲': 'dice', '✨': 'sparkle',
  '♟': 'swords', '🎯': 'target', '👥': 'users', '📷': 'camera', '🎵': 'mic', '🔗': 'link', '💎': 'gem', '💠': 'gem', '🧠': 'brain',
  '⏱': 'timer', '🌱': 'sprout', '🌙': 'moon', '📞': 'phone', '📹': 'video', '🤝': 'handshake', '💪': 'muscle', '🙂': 'smile', '😎': 'smile',
}
export const posterNameFor = (emoji: string): string => EMOJI_TO_NAME[(emoji || '').replace(/\uFE0F/g, '')] || 'star'

export function PosterIcon({ name, size = 28, className, style }: { name: string; size?: number; className?: string; style?: CSSProperties }) {
  const d = DEFS[name] || DEFS.star
  const G = d.glyph
  const id = `pi-${name}`
  return (
    <span className={className} aria-hidden="true" style={{ display: 'inline-grid', placeItems: 'center', width: size, height: size, flex: 'none', verticalAlign: 'middle', ...style }}>
      <svg width={size} height={size} viewBox="0 0 48 48" role="img" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={d.a} /><stop offset="1" stopColor={d.b} /></linearGradient>
          <linearGradient id={`${id}-g`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#fff" stopOpacity=".55" /><stop offset="1" stopColor="#fff" stopOpacity="0" /></linearGradient>
        </defs>
        <rect x="2" y="2" width="44" height="44" rx="13" fill={`url(#${id})`} />
        <rect x="2" y="2" width="44" height="22" rx="13" fill={`url(#${id}-g)`} />
        <rect x="2.5" y="2.5" width="43" height="43" rx="12.5" fill="none" stroke="#fff" strokeOpacity=".28" />
        <g transform="translate(11 11)"><G size={26} strokeWidth={2.4} color="#fff" /></g>
      </svg>
    </span>
  )
}

/** Same, but picked from an emoji character. */
export function EmojiPoster({ char, size = 28, className, style }: { char: string; size?: number; className?: string; style?: CSSProperties }) {
  return <PosterIcon name={posterNameFor(char)} size={size} className={className} style={style} />
}
